// Squad planner — czysta logika: pozycje planszy, role, kontrakty, struktura kadry,
// odejścia, automatyczne rozstawienie i operacje na planszy (bez Reacta).

import { hasAccess, isAdmin, type Me } from '../lib/auth'
import type { Board, BoardEntry, BoardSlots, Category, Clause, ClauseKind, Formation, PlanRole, SlotKey, SquadPlayer, Targets } from './types'

export type Lang = 'pl' | 'en'

// --- Uprawnienia ---------------------------------------------------------------
// Squad planner API pilnuje ZAWSZE (także przy wyłączonej fladze AUTH_REQUIRED), więc
// front czyta prawdziwy dostęp konta (`me.access`), a nie `useCan` — ten przy wyłączonej
// fladze mówi „wszystko wolno" i pokazywałby kontrolki, na które API odpowie 403.
// Co widać z danych (daty kontraktów, klauzule), mówią flagi z odpowiedzi /api/squad/players.

export type SquadTab = 'board' | 'age' | 'contracts'

export const SQUAD_TABS: { key: SquadTab; section: string; label: string }[] = [
  { key: 'board', section: 'squad.planner', label: 'Plansza' },
  { key: 'age', section: 'squad.wiek', label: 'Wiek × minuty' },
  { key: 'contracts', section: 'squad.kontrakty', label: 'Kontrakty' },
]

export interface SquadPerms {
  /** Dostępne zakładki w kolejności SQUAD_TABS (pusta = brak wejścia do Squad plannera). */
  tabs: SquadTab[]
  /** Własne plany: zakładanie, plansza, usuwanie (Edycja `squad.planner`). */
  editPlans: boolean
  /** Korekty kontraktów (Edycja `squad.kontrakty`). */
  editContracts: boolean
  /** Dodawanie / zmiana / usuwanie klauzul (Edycja `squad.klauzule`). */
  editClauses: boolean
}

export function squadPerms(me: Me | null): SquadPerms {
  const admin = isAdmin(me)
  const can = (section: string, level: 'view' | 'edit' = 'view') => admin || hasAccess(me, section, level)
  return {
    tabs: SQUAD_TABS.filter((t) => can(t.section)).map((t) => t.key),
    editPlans: can('squad.planner', 'edit'),
    editContracts: can('squad.kontrakty', 'edit'),
    editClauses: can('squad.klauzule', 'edit'),
  }
}

/** Zakładka z adresu, o ile dostępna — inaczej pierwsza dostępna (null = żadnej). */
export function pickTab(requested: string | null, tabs: SquadTab[]): SquadTab | null {
  return tabs.find((t) => t === requested) ?? tabs[0] ?? null
}

// --- Klauzule ------------------------------------------------------------------

export const CLAUSE_KINDS: ClauseKind[] = ['wykup', 'przedluzenie', 'odstepne', 'inna']
export const CLAUSE_LABEL: Record<ClauseKind, { pl: string; en: string }> = {
  wykup: { pl: 'Wykup', en: 'Buy option' },
  przedluzenie: { pl: 'Automatyczne przedłużenie', en: 'Automatic extension' },
  odstepne: { pl: 'Odstępne', en: 'Release clause' },
  inna: { pl: 'Inna', en: 'Other' },
}

/** Skrót do kolumny tabeli: rodzaje w kolejności CLAUSE_KINDS, powtórzone z krotnością („Wykup ×2 · Odstępne"). */
export function clauseSummary(clauses: Clause[] | null | undefined): string {
  if (!clauses?.length) return ''
  return CLAUSE_KINDS.map((k) => {
    const n = clauses.filter((c) => c.kind === k).length
    return n ? `${CLAUSE_LABEL[k].pl}${n > 1 ? ` ×${n}` : ''}` : ''
  }).filter(Boolean).join(' · ')
}

// --- Plansza: 3-4-3 i 3-5-2 ---------------------------------------------------
// Nazwy jak na slajdach sztabu (WA = skrzydłowy przy napastniku). `x` = środek kolumny
// jako ułamek szerokości boiska, `row` = linia (0 atak … 3 bramkarz), `drop` = zejście
// kolumny w dół w obrębie linii (skrzydłowi stoją niżej niż napastnik, „6" niżej niż „8").
// Pozycje wspólne obu ustawień (GK, trójka stoperów, wahadła, LCM/RCM) mają ten sam klucz.

export interface SlotDef { key: SlotKey; label: string; x: number; row: 0 | 1 | 2 | 3; drop?: number; pl: string; en: string }

const BACK: SlotDef[] = [
  { key: 'LCB', label: 'LCB', x: 0.23, row: 2, pl: 'Lewy stoper', en: 'Left centre-back' },
  { key: 'CB', label: 'CB', x: 0.5, row: 2, pl: 'Środkowy stoper', en: 'Centre-back' },
  { key: 'RCB', label: 'RCB', x: 0.77, row: 2, pl: 'Prawy stoper', en: 'Right centre-back' },
  { key: 'GK', label: 'GK', x: 0.5, row: 3, pl: 'Bramkarz', en: 'Goalkeeper' },
]

export const FORMATIONS: Formation[] = ['3-4-3', '3-5-2']

export const FORMATION_SLOTS: Record<Formation, SlotDef[]> = {
  '3-4-3': [
    { key: 'ST', label: 'ST', x: 0.5, row: 0, pl: 'Napastnik', en: 'Striker' },
    { key: 'LWA', label: 'WA', x: 0.2, row: 0, drop: 44, pl: 'Lewy skrzydłowy', en: 'Left winger' },
    { key: 'RWA', label: 'WA', x: 0.8, row: 0, drop: 44, pl: 'Prawy skrzydłowy', en: 'Right winger' },
    { key: 'LWB', label: 'LWB', x: 0.09, row: 1, pl: 'Lewy wahadłowy', en: 'Left wing-back' },
    { key: 'LCM', label: 'CM', x: 0.365, row: 1, pl: 'Środkowy pomocnik (L)', en: 'Central midfielder (L)' },
    { key: 'RCM', label: 'CM', x: 0.635, row: 1, pl: 'Środkowy pomocnik (P)', en: 'Central midfielder (R)' },
    { key: 'RWB', label: 'RWB', x: 0.91, row: 1, pl: 'Prawy wahadłowy', en: 'Right wing-back' },
    ...BACK,
  ],
  '3-5-2': [
    { key: 'LST', label: 'ST', x: 0.36, row: 0, pl: 'Napastnik (L)', en: 'Striker (L)' },
    { key: 'RST', label: 'ST', x: 0.64, row: 0, pl: 'Napastnik (P)', en: 'Striker (R)' },
    { key: 'LWB', label: 'LWB', x: 0.09, row: 1, pl: 'Lewy wahadłowy', en: 'Left wing-back' },
    { key: 'LCM', label: 'CM', x: 0.29, row: 1, pl: 'Środkowy pomocnik (L)', en: 'Central midfielder (L)' },
    { key: 'DM', label: 'DM', x: 0.5, row: 1, drop: 44, pl: 'Defensywny pomocnik', en: 'Defensive midfielder' },
    { key: 'RCM', label: 'CM', x: 0.71, row: 1, pl: 'Środkowy pomocnik (P)', en: 'Central midfielder (R)' },
    { key: 'RWB', label: 'RWB', x: 0.91, row: 1, pl: 'Prawy wahadłowy', en: 'Right wing-back' },
    ...BACK,
  ],
}

/** Pozycje planszy w jej ustawieniu (plan sprzed 3-5-2 nie ma `formation` innego niż 3-4-3). */
export const slotsOf = (board: Pick<Board, 'formation'> | Formation): SlotDef[] =>
  FORMATION_SLOTS[typeof board === 'string' ? board : board.formation] ?? FORMATION_SLOTS['3-4-3']

/** Słownik wszystkich pozycji (etykieta, nazwa) — bez położenia, bo to zależy od ustawienia. */
export const SLOT_BY_KEY = Object.fromEntries(
  FORMATIONS.flatMap((f) => FORMATION_SLOTS[f]).map((s) => [s.key, s]),
) as Record<SlotKey, SlotDef>

/** Gdzie trafia NOWY wpis z pozycją z innego ustawienia: „10" → „8",
 *  napastnik → dwójka napastników; w drugą stronę „6" → środek, dwójka → jeden napastnik.
 *  Przy kilku kandydatach — mniej obsadzony. */
const SLOT_FALLBACK: Partial<Record<SlotKey, SlotKey[]>> = {
  LWA: ['LCM'], RWA: ['RCM'], ST: ['LST', 'RST'],
  DM: ['LCM', 'RCM'], LST: ['ST'], RST: ['ST'],
}

const leastFilled = (board: Board, pool: SlotKey[]) =>
  pool.reduce((best, s) => (slotEntries(board, s).length < slotEntries(board, best).length ? s : best))

/** Pozycja w ustawieniu planszy dla klucza, który może pochodzić z innego ustawienia. */
export function adaptSlot(slot: SlotKey, board: Board): SlotKey {
  const keys = slotsOf(board).map((s) => s.key)
  if (keys.includes(slot)) return slot
  const pool = (SLOT_FALLBACK[slot] ?? []).filter((k) => keys.includes(k))
  return pool.length ? leastFilled(board, pool) : 'CB'
}

/** Zmiana ustawienia. Każde ustawienie ma WŁASNE rozstawienie — plansza bieżąca odkłada
 *  się do `stored`, a wraca ta zapisana pod `to`; nikt nie przeskakuje między pozycjami.
 *  Pierwsze otwarcie ustawienia bierze z bieżącego tylko pozycje wspólne (kopia z nowymi
 *  uid), reszta zawodników czeka w puli; odtąd oba rozstawienia są niezależne. */
export function changeFormation(board: Board, to: Formation): Board {
  if (board.formation === to) return board
  const { [to]: saved, ...rest } = board.stored ?? {}
  return {
    formation: to,
    slots: saved ?? cloneSlots(board.slots, slotsOf(to).map((s) => s.key)),
    stored: { ...rest, [board.formation]: board.slots },
  }
}

// --- Role i struktura ------------------------------------------------------

export const ROLES: PlanRole[] = ['key', 'starter', 'potential', 'rotation', 'leave']
export const ROLE_META: Record<PlanRole, { color: string; tint: string; pl: string; en: string }> = {
  key: { color: '#0E7A43', tint: '#E2F2E8', pl: 'Kluczowy', en: 'Key starter' },
  starter: { color: '#1F7FC4', tint: '#E3EFF9', pl: 'Podstawowy', en: 'Starter' },
  potential: { color: '#E08A0B', tint: '#FCEFD9', pl: 'Wysoki potencjał', en: 'High potential' },
  rotation: { color: '#9358C2', tint: '#F0E7F8', pl: 'Rotacja', en: 'Rotation' },
  leave: { color: '#C8102E', tint: '#FBE2E6', pl: 'Odejście', en: 'Leave / out' },
}
export const VACANCY = { color: '#E3B505', tint: '#FFF5CC', pl: 'Wakat', en: 'Vacancy' }

export const CATEGORIES: Category[] = ['core', 'talent', 'gk']
export const CATEGORY_META: Record<Category, { pl: string; en: string; plShort: string; enShort: string }> = {
  core: { pl: 'Trzon', en: 'Core', plShort: 'trzon', enShort: 'core' },
  talent: { pl: 'Talenty', en: 'Talents', plShort: 'talenty', enShort: 'talents' },
  gk: { pl: 'Bramkarze', en: 'Goalkeepers', plShort: 'GK', enShort: 'GK' },
}
/** Talent = do 21 lat w sezonie planu (rocznikowo), o ile sztab nie wskaże inaczej. */
export const TALENT_MAX_AGE = 21
export const DEFAULT_TARGETS: Targets = { core: 18, talent: 6, gk: 3 }

// --- Sezon i wiek ----------------------------------------------------------

export const seasonStart = (label: string) => parseInt(label.slice(0, 4), 10)
export const seasonLabel = (start: number) => `${start}/${String((start + 1) % 100).padStart(2, '0')}`
/** Wiek rocznikowy w sezonie planu — ta sama reguła co „2026 − rocznik" na wykresie wiek × minuty. */
export const ageIn = (birthYear: number | null | undefined, start: number) => (birthYear ? start - birthYear : null)

// --- Kontrakty ---------------------------------------------------------------

export interface LegiaContract {
  kind: 'own' | 'loan_in' | 'loan_out'
  /** Do kiedy zawodnik jest NASZ: kontrakt z Legią albo — u wypożyczonego do nas — koniec wypożyczenia. */
  until: string | null
  untilManual: boolean
  /** Opcja przedłużenia (własny) albo wykupu (wypożyczony do nas). */
  option: string | null
  /** loan_in: klub macierzysty; loan_out: klub, w którym gra. */
  loanClub: string | null
  /** loan_out: koniec wypożyczenia w tamtym klubie. */
  loanUntil: string | null
  lastExtension: string | null
  note: string | null
}

export function legiaContract(p: SquadPlayer): LegiaContract {
  const o = p.override
  const tm = p.tm
  const base = { option: o?.option_note ?? tm?.option ?? null, lastExtension: tm?.last_extension ?? null, note: o?.note ?? null }
  if (p.status === 'loan_in') {
    const manual = o?.loan_until ?? o?.contract_until ?? null
    const until = manual ?? tm?.loan_until ?? null
    return { ...base, kind: 'loan_in', until, untilManual: !!manual, loanClub: tm?.loan_from ?? null, loanUntil: until }
  }
  const manual = o?.contract_until ?? null
  const until = manual ?? tm?.contract_until ?? null
  if (p.status === 'loan_out') {
    return { ...base, kind: 'loan_out', until, untilManual: !!manual, loanClub: tm?.main_team ?? null,
      loanUntil: o?.loan_until ?? tm?.loan_until ?? null }
  }
  return { ...base, kind: 'own', until, untilManual: !!manual, loanClub: null, loanUntil: null }
}

export type Expiry = 'before' | 'during' | 'later' | 'unknown'
/** before = kończy się przed startem sezonu planu (decyzja potrzebna teraz),
 *  during = wygasa w trakcie / na koniec sezonu planu, later = dłużej. */
export function expiryIn(until: string | null | undefined, start: number): Expiry {
  if (!until) return 'unknown'
  if (until < `${start}-07-01`) return 'before'
  if (until < `${start + 1}-07-01`) return 'during'
  return 'later'
}

/** Kolor kontraktu jak na wykresie wiek × minuty: koniec sezonu planu / rok później / dłużej. */
export type ContractBucket = 1 | 2 | 3 | null
export function contractBucket(until: string | null | undefined, start: number): ContractBucket {
  if (!until) return null
  const y = parseInt(until.slice(0, 4), 10)
  return y <= start + 1 ? 1 : y === start + 2 ? 2 : 3
}
export const BUCKET_COLOR: Record<string, string> = { 1: '#1FA4DC', 2: '#D6DC2B', 3: '#C8102E', null: '#A9B0B7' }

export const fmtMonthYear = (iso: string | null | undefined) => (iso ? `${iso.slice(5, 7)}.${iso.slice(0, 4)}` : '—')
export const fmtDate = (iso: string | null | undefined) => (iso ? `${iso.slice(8, 10)}.${iso.slice(5, 7)}.${iso.slice(0, 4)}` : '—')
export const fmtYearShort = (iso: string | null | undefined) => (iso ? `’${iso.slice(2, 4)}` : '–')

const OPTION_TEXT: Record<string, { pl: string; en: string }> = {
  'Option for a further year': { pl: 'opcja +1 rok', en: 'option +1 year' },
  'Option to buy': { pl: 'opcja wykupu', en: 'option to buy' },
  'Obligation to buy': { pl: 'obowiązek wykupu', en: 'obligation to buy' },
  'Obligation to buy with conditions': { pl: 'warunkowy obowiązek wykupu', en: 'conditional obligation to buy' },
}
export const optionText = (opt: string | null | undefined, lang: Lang) => (opt ? OPTION_TEXT[opt]?.[lang] ?? opt : null)

// Transfermarkt podaje kluby i agentów po niemiecku / bez polskich znaków.
const CLUB_TEXT: Record<string, { pl: string; en: string }> = {
  'AC Sparta Prag': { pl: 'Sparta Praga', en: 'Sparta Prague' },
  'Lechia Gdansk': { pl: 'Lechia Gdańsk', en: 'Lechia Gdańsk' },
  'Pogon Grodzisk Mazowiecki': { pl: 'Pogoń Grodzisk', en: 'Pogoń Grodzisk' },
  'Legia Warschau': { pl: 'Legia Warszawa', en: 'Legia Warszawa' },
  'Legia Warschau II': { pl: 'Legia II', en: 'Legia II' },
}
export const clubName = (name: string | null | undefined, lang: Lang) => (name ? CLUB_TEXT[name]?.[lang] ?? name : null)

const AGENT_TEXT: Record<string, { pl: string; en: string }> = {
  'ohne Berater': { pl: 'bez agenta', en: 'no agent' },
  'Familienangehörige': { pl: 'rodzina', en: 'family' },
  'Familienangehöriger': { pl: 'rodzina', en: 'family' },
}
export const agentName = (name: string | null | undefined, lang: Lang) => (name ? AGENT_TEXT[name]?.[lang] ?? name : null)

export function fmtMoney(v: number | null | undefined, lang: Lang): string {
  if (!v) return '—'
  if (v >= 1_000_000) {
    const m = (v / 1_000_000).toFixed(v % 1_000_000 === 0 ? 0 : 1)
    return lang === 'pl' ? `${m.replace('.', ',')} mln €` : `€${m}m`
  }
  const k = Math.round(v / 1000)
  return lang === 'pl' ? `${k} tys. €` : `€${k}k`
}

// --- Etykiety zawodników -----------------------------------------------------

/** Nazwisko, a przy zdublowanym nazwisku w puli — z inicjałem („W. Urbański"). */
export function surnameLabels(players: SquadPlayer[]): Map<string, string> {
  const count = new Map<string, number>()
  for (const p of players) count.set(p.last_name, (count.get(p.last_name) ?? 0) + 1)
  return new Map(players.map((p) => [
    p.id, (count.get(p.last_name) ?? 0) > 1 && p.first_name ? `${p.first_name[0]}. ${p.last_name}` : p.last_name,
  ]))
}

// --- Operacje na planszy ------------------------------------------------------

export function newUid(): string {
  const c = globalThis.crypto
  if (c?.randomUUID) return c.randomUUID().slice(0, 13)
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`
}

export const emptyBoard = (): Board => ({ formation: '3-4-3', slots: {} })

export const slotEntries = (board: Board, slot: SlotKey): BoardEntry[] => board.slots[slot] ?? []

export function findEntry(board: Board, uid: string): { slot: SlotKey; index: number; entry: BoardEntry } | null {
  for (const s of slotsOf(board)) {
    const list = board.slots[s.key] ?? []
    const index = list.findIndex((e) => e.uid === uid)
    if (index >= 0) return { slot: s.key, index, entry: list[index] }
  }
  return null
}

export function placedPlayerIds(board: Board): Set<string> {
  const out = new Set<string>()
  for (const list of Object.values(board.slots)) for (const e of list ?? []) if (e.kind === 'player' && e.player_id) out.add(e.player_id)
  return out
}

export function insertEntry(board: Board, entry: BoardEntry, slot: SlotKey, index?: number): Board {
  const list = [...slotEntries(board, slot)]
  list.splice(index ?? list.length, 0, entry)
  return { ...board, slots: { ...board.slots, [slot]: list } }
}

export function removeEntry(board: Board, uid: string): Board {
  const hit = findEntry(board, uid)
  if (!hit) return board
  const list = slotEntries(board, hit.slot).filter((e) => e.uid !== uid)
  return { ...board, slots: { ...board.slots, [hit.slot]: list } }
}

/** Przeniesienie wpisu; `index` liczony w docelowej liście PRZED wyjęciem wpisu. */
export function moveEntry(board: Board, uid: string, toSlot: SlotKey, index?: number): Board {
  const hit = findEntry(board, uid)
  if (!hit) return board
  let at = index
  if (at !== undefined && hit.slot === toSlot && hit.index < at) at -= 1
  return insertEntry(removeEntry(board, uid), hit.entry, toSlot, at)
}

export function updateEntry(board: Board, uid: string, patch: Partial<BoardEntry>): Board {
  const hit = findEntry(board, uid)
  if (!hit) return board
  const list = slotEntries(board, hit.slot).map((e) => (e.uid === uid ? { ...e, ...patch } : e))
  return { ...board, slots: { ...board.slots, [hit.slot]: list } }
}

// --- Automatyczne rozstawienie --------------------------------------------------
// Rodzina pozycji z grupy kadry (BR / ŚO / 7/11 / 8 / 10 / 9), strona z najdłużej granej
// pozycji StatsBomba; bez wskazania strony — tam, gdzie mniej zawodników. W kolumnie
// kolejność wg minut bieżącego sezonu. Wypożyczeni Z Legii zostają poza planszą.

const SB_POS_SLOTS: Record<number, SlotKey[]> = {
  1: ['GK'], 2: ['RWB'], 3: ['RCB'], 4: ['CB'], 5: ['LCB'], 6: ['LWB'], 7: ['RWB'], 8: ['LWB'],
  9: ['RCM'], 10: ['LCM', 'RCM'], 11: ['LCM'], 12: ['RWB'], 13: ['RCM'], 14: ['LCM', 'RCM'], 15: ['LCM'],
  16: ['LWB'], 17: ['RWA'], 18: ['RWA'], 19: ['LWA', 'RWA'], 20: ['LWA'], 21: ['LWA'],
  22: ['ST'], 23: ['ST'], 24: ['ST'], 25: ['ST'],
}
const GROUP_SLOTS: Record<string, SlotKey[]> = {
  BR: ['GK'], 'ŚO': ['LCB', 'CB', 'RCB'], '7/11': ['LWB', 'RWB'], '8': ['LCM', 'RCM'], '10': ['LWA', 'RWA'], '9': ['ST'],
}

/** Pozycja dla zawodnika: rodzina z grupy kadry, strona ze StatsBomba, przy remisie mniej obsadzona. */
export function suggestSlot(p: SquadPlayer, board: Board): SlotKey | null {
  const bySb = p.sb_position_id ? SB_POS_SLOTS[p.sb_position_id] ?? [] : []
  const family = (p.position && GROUP_SLOTS[p.position]) || bySb
  if (!family.length) return null
  const side = bySb.filter((s) => family.includes(s))
  const pool = side.length ? side : family
  // w 3-5-2 „szóstka" (defensywny pomocnik w StatsBombie) staje na DM
  if (board.formation === '3-5-2' && family.includes('LCM') && p.sb_position_id && p.sb_position_id >= 9 && p.sb_position_id <= 11) return 'DM'
  return adaptSlot(leastFilled(board, pool), board)
}

/** Pozycja dla zawodnika spoza kadry z `mainPosition` Transfermarktu. */
const TM_POS_SLOTS: Record<string, SlotKey[]> = {
  goalkeeper: ['GK'], centerback: ['CB', 'LCB', 'RCB'], libero: ['CB'], leftback: ['LWB'], rightback: ['RWB'],
  leftmidfield: ['LWB'], rightmidfield: ['RWB'], defensivemidfield: ['LCM', 'RCM'], centralmidfield: ['LCM', 'RCM'],
  attackingmidfield: ['LWA', 'RWA'], leftwing: ['LWA'], rightwing: ['RWA'], secondstriker: ['ST'], centerforward: ['ST'],
}
export function suggestSlotTm(position: string | null | undefined, board: Board): SlotKey {
  if (board.formation === '3-5-2' && position === 'defensivemidfield') return 'DM'
  const pool = (position && TM_POS_SLOTS[position]) || ['CB']
  return adaptSlot(leastFilled(board, pool), board)
}
const TM_POS_PL: Record<string, string> = {
  goalkeeper: 'BR', centerback: 'ŚO', libero: 'ŚO', leftback: 'LO', rightback: 'PO', leftmidfield: 'LP', rightmidfield: 'PP',
  defensivemidfield: 'ŚPD', centralmidfield: 'ŚP', attackingmidfield: 'ŚPO', leftwing: 'LS', rightwing: 'PS',
  secondstriker: 'CN', centerforward: 'N',
}
export const tmPositionLabel = (position: string | null | undefined) => (position ? TM_POS_PL[position] ?? position : '—')

export function autoBoard(players: SquadPlayer[]): Board {
  let board = emptyBoard()
  const ordered = players
    .filter((p) => p.status !== 'loan_out')
    .sort((a, b) => b.minutes.minutes - a.minutes.minutes || (a.birth_year ?? 0) - (b.birth_year ?? 0))
  for (const p of ordered) {
    const slot = suggestSlot(p, board)
    if (slot) board = insertEntry(board, { uid: newUid(), kind: 'player', player_id: p.id }, slot)
  }
  return board
}

/** Kopia wpisów z podanych pozycji — z nowymi uid. */
function cloneSlots(slots: BoardSlots, keys: SlotKey[]): BoardSlots {
  const out: BoardSlots = {}
  for (const k of keys) {
    const list = slots[k]
    if (list?.length) out[k] = list.map((e) => ({ ...e, uid: newUid() }))
  }
  return out
}

/** Kopia planszy pod inny plan — nowe uid, te same wpisy, oba ustawienia. */
export function cloneBoard(board: Board): Board {
  const stored: Board['stored'] = {}
  for (const f of FORMATIONS) {
    const saved = board.stored?.[f]
    if (saved && f !== board.formation) stored[f] = cloneSlots(saved, slotsOf(f).map((s) => s.key))
  }
  return { formation: board.formation ?? '3-4-3', slots: cloneSlots(board.slots, slotsOf(board).map((s) => s.key)), stored }
}

// --- Analiza planu ----------------------------------------------------------------

export interface PlanContext {
  start: number
  byId: Map<string, SquadPlayer>
}

export const vacancyCount = (e: BoardEntry) => {
  const m = (e.count ?? '').match(/\d+/)
  return m ? Math.max(1, parseInt(m[0], 10)) : 1
}

export function entryAge(e: BoardEntry, ctx: PlanContext): number | null {
  if (e.kind === 'player') return ageIn(ctx.byId.get(e.player_id ?? '')?.birth_year, ctx.start)
  if (e.kind === 'manual') return ageIn(e.birth_year, ctx.start)
  return null
}

export function entryCategory(e: BoardEntry, slot: SlotKey, ctx: PlanContext): Category {
  if (e.category) return e.category
  if (slot === 'GK') return 'gk'
  if (e.kind === 'vacancy') return 'core'
  const age = entryAge(e, ctx)
  return age !== null && age <= TALENT_MAX_AGE ? 'talent' : 'core'
}

/** Do kiedy wpis jest nasz (zawodnik z kadry albo wpis ręczny). */
/** Wpis spoza kadry z klubem: jego kontrakt dotyczy tamtego klubu, nie Legii. */
export const isOutsider = (e: BoardEntry) => e.kind === 'manual' && !!e.club

export function entryUntil(e: BoardEntry, ctx: PlanContext): string | null {
  if (e.kind === 'manual') return e.contract_until ?? null
  const p = e.kind === 'player' ? ctx.byId.get(e.player_id ?? '') : undefined
  return p ? legiaContract(p).until : null
}

export interface StructureRow { players: number; vacancies: number; target: number }

/** Liczebność struktury: zawodnicy bez roli „odejście" + wakaty, wobec celu planu. */
export function structure(board: Board, ctx: PlanContext, targets: Targets): Record<Category, StructureRow> {
  const out: Record<Category, StructureRow> = {
    core: { players: 0, vacancies: 0, target: targets.core },
    talent: { players: 0, vacancies: 0, target: targets.talent },
    gk: { players: 0, vacancies: 0, target: targets.gk },
  }
  for (const s of slotsOf(board)) {
    for (const e of slotEntries(board, s.key)) {
      const cat = entryCategory(e, s.key, ctx)
      if (e.kind === 'vacancy') out[cat].vacancies += vacancyCount(e)
      else if (e.role !== 'leave') out[cat].players += 1
    }
  }
  return out
}

export interface ListedEntry { uid: string; slot: SlotKey; entry: BoardEntry; until: string | null; age: number | null }

/** Odejścia (rola „odejście") i decyzje do podjęcia: zawodnicy bez tej roli, którym
 *  kontrakt albo wypożyczenie kończy się przed startem sezonu planu. */
export function departures(board: Board, ctx: PlanContext): { leaving: ListedEntry[]; decide: ListedEntry[] } {
  const leaving: ListedEntry[] = []
  const decide: ListedEntry[] = []
  for (const s of slotsOf(board)) {
    for (const e of slotEntries(board, s.key)) {
      if (e.kind === 'vacancy') continue
      const item = { uid: e.uid, slot: s.key, entry: e, until: entryUntil(e, ctx), age: entryAge(e, ctx) }
      if (e.role === 'leave') leaving.push(item)
      else if (!isOutsider(e) && expiryIn(item.until, ctx.start) === 'before') decide.push(item)
    }
  }
  return { leaving, decide }
}

export function vacancies(board: Board): { slot: SlotKey; entry: BoardEntry }[] {
  return slotsOf(board).flatMap((s) => slotEntries(board, s.key).filter((e) => e.kind === 'vacancy').map((entry) => ({ slot: s.key, entry })))
}

// --- Wyszukiwanie zawodnika na planszy ------------------------------------------

export interface PlanLookup { slotOf: Map<string, SlotKey>; entryOf: Map<string, BoardEntry> }

export function planLookup(board: Board): PlanLookup {
  const slotOf = new Map<string, SlotKey>()
  const entryOf = new Map<string, BoardEntry>()
  for (const s of slotsOf(board)) {
    for (const e of slotEntries(board, s.key)) if (e.kind === 'player' && e.player_id) { slotOf.set(e.player_id, s.key); entryOf.set(e.player_id, e) }
  }
  return { slotOf, entryOf }
}
